import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Factory,
  FileDown,
  FileSpreadsheet,
  FileText,
  Landmark,
  LucideAngularModule,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
  XCircle,
  Zap,
} from 'lucide-angular';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../core/constants/pagination.constants';
import { VendorsService } from '../services/vendors.service';
import { Vendor } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { VENDOR_TYPES } from './vendor.constants';

type VendorStatusFilter = 'all' | 'approved' | 'pending' | 'rejected';
type SummaryTile = {
  label: string;
  count: number;
  icon: typeof Building2;
  approved: number;
  pending: number;
  rejected: number;
  vendorType?: string;
  status?: VendorStatusFilter;
};

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './vendors.page.html',
  styleUrl: './vendors.page.scss',
})
export class VendorsPage {
  protected readonly router = inject(Router);
  private readonly vendorsService = inject(VendorsService);
  private readonly toast = inject(ToastService);

  protected readonly SearchIcon = Search;
  protected readonly ChevronsLeftIcon = ChevronsLeft;
  protected readonly ChevronsRightIcon = ChevronsRight;
  protected readonly ChevronLeftIcon = ChevronLeft;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly PlusIcon = Plus;
  protected readonly FileDownIcon = FileSpreadsheet;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;
  protected readonly FileTextIcon = FileText;

  protected readonly Building2Icon = Building2;
  protected readonly LandmarkIcon = Landmark;
  protected readonly FactoryIcon = Factory;
  protected readonly ZapIcon = Zap;
  protected readonly PowerIcon = Power;
  protected readonly XCircleIcon = XCircle;

  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly selectedVendor = signal<Vendor | null>(null);
  protected readonly search = signal('');
  protected readonly vendorType = signal('all');
  protected readonly vendorStatus = signal<VendorStatusFilter>('all');
  protected readonly page = signal(1);
  protected readonly pageSize = signal(10);
  protected readonly pageSizes = DEFAULT_PAGE_SIZE_OPTIONS;
  protected readonly loading = signal(true);

  protected readonly vendorTypes = computed(() => VENDOR_TYPES);

  protected readonly filteredVendors = computed(() => {
    const term = this.search().toLowerCase();
    const type = this.vendorType();
    const status = this.vendorStatus();
    return this.vendors().filter((vendor) => {
      const matchesSearch =
        vendor.vendor_name.toLowerCase().includes(term) ||
        vendor.email.toLowerCase().includes(term) ||
        (vendor.noar_id || '').toLowerCase().includes(term);
      const matchesType = type === 'all' || vendor.vendor_type === type;
      const matchesStatus = status === 'all' || vendor.status === status;
      return matchesSearch && matchesType && matchesStatus;
    });
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredVendors().length / this.pageSize())));
  protected readonly pagedVendors = computed(() => {
    const page = Math.min(this.page(), this.totalPages());
    const start = (page - 1) * this.pageSize();
    return this.filteredVendors().slice(start, start + this.pageSize());
  });
  protected readonly startRow = computed(() => (this.filteredVendors().length === 0 ? 0 : (this.page() - 1) * this.pageSize() + 1));
  protected readonly endRow = computed(() => Math.min(this.page() * this.pageSize(), this.filteredVendors().length));

  protected readonly summaryTiles = computed(() => {
    const data = this.vendors();
    const byStatus = (rows: Vendor[], status: string) => rows.filter((item) => item.status === status).length;
    const createTile = (label: string, rows: Vendor[], icon: typeof Building2, filters: Partial<Pick<SummaryTile, 'vendorType' | 'status'>> = {}): SummaryTile => ({
      label,
      count: rows.length,
      icon,
      approved: byStatus(rows, 'approved'),
      pending: byStatus(rows, 'pending'),
      rejected: byStatus(rows, 'rejected'),
      ...filters,
    });
    return [
      createTile('Total DISCOM', data.filter((item) => item.vendor_type === 'DISCOM'), this.LandmarkIcon, { vendorType: 'DISCOM' }),
      createTile('Total Industries', data.filter((item) => item.vendor_type === 'Industry'), this.FactoryIcon, { vendorType: 'Industry' }),
      createTile('Total Generator', data.filter((item) => item.vendor_type === 'Generator'), this.ZapIcon, { vendorType: 'Generator' }),
      createTile('Total IPP', data.filter((item) => item.vendor_type === 'IPP'), this.PowerIcon, { vendorType: 'IPP' }),
      createTile('Total Rejected', data.filter((item) => item.status === 'rejected'), this.XCircleIcon, { status: 'rejected' }),
      createTile('Total Vendors', data, this.Building2Icon, { vendorType: 'all', status: 'all' }),
    ];
  });

  constructor() {
    this.load();
  }

  private load(): void {
    this.loading.set(true);
    this.vendorsService.list().subscribe({
      next: (vendors) => {
        this.vendors.set(vendors || []);
        this.loading.set(false);
      },
      error: () => {
        this.vendors.set([]);
        this.loading.set(false);
        this.toast.show('Failed to load vendors', '', 'danger');
      },
    });
  }

  protected onSearch(value: string): void {
    this.search.set(value);
    this.page.set(1);
  }

  protected onTypeChange(value: string): void {
    this.vendorType.set(value);
    this.vendorStatus.set('all');
    this.page.set(1);
  }

  protected applySummaryFilter(tile: SummaryTile): void {
    this.vendorType.set(tile.vendorType || 'all');
    this.vendorStatus.set(tile.status || 'all');
    this.page.set(1);
  }

  protected onPageSizeChange(value: string): void {
    this.pageSize.set(Number(value));
    this.page.set(1);
  }

  protected removeVendor(vendor: Vendor): void {
    if (vendor.can_delete === false) {
      this.toast.show(vendor.delete_disabled_reason || 'Vendor cannot be deleted', '', 'info');
      return;
    }
    if (!confirm(`Delete vendor ${vendor.vendor_name}?`)) return;
    this.vendorsService.delete(vendor.id).subscribe({
      next: () => {
        this.toast.show('Vendor deleted', '', 'success');
        this.load();
      },
      error: (error) => this.toast.show(error?.error?.detail || 'Failed to delete vendor', '', 'danger'),
    });
  }

  protected deleteTitle(vendor: Vendor): string {
    return vendor.can_delete === false ? (vendor.delete_disabled_reason || 'Vendor cannot be deleted') : 'Delete';
  }

  protected exportVendors(): void {
    const headers = ['S.No.', 'Client Name', 'Type', 'Generator Type', 'State', 'Region', 'NOAR ID', 'Status'];
    const rows = this.filteredVendors().map((vendor, index) => [
      index + 1,
      vendor.vendor_name,
      vendor.vendor_type,
      vendor.generator_type || '',
      vendor.state || '',
      vendor.region || '',
      vendor.noar_id || '',
      vendor.status,
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vendors_export_${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  protected openVendorDetails(vendor: Vendor): void {
    this.selectedVendor.set(vendor);
  }

  protected closeVendorDetails(): void {
    this.selectedVendor.set(null);
  }

  protected segmentWidth(value: number, total: number): number {
    if (!total || !value) return 0;
    return (value / total) * 100;
  }
}
